/*
  A mapping collection is a list of models that associate assets with an asset tag value.
*/
import { action, computed } from 'mobx';
import { differenceWith } from 'lodash';
import { AssetType } from '@kentik/ui-shared/labels/lib';
import $assetTags from 'app/stores/assetTags/$assetTags';
import { Collection } from 'core/model/Collection';
import { AssetCollectionModel } from './$assetTags.types';
import AssetTagValueModel from './AssetTagValueModel';
import AssetTagValueMappingModel from './AssetTagValueMappingModel';

class AssetTagValueMappingCollection extends Collection<AssetTagValueMappingModel> {
  // keep the value associated with the mappings - together they make up a complete asset tag value in the db
  valueModel: AssetTagValueModel;

  constructor(data: AssetTagValueMappingModel[] = [], { valueModel }: { valueModel: AssetTagValueModel }) {
    super(data);
    this.valueModel = valueModel;
  }

  get model() {
    return AssetTagValueMappingModel;
  }

  getFilterValues(model: AssetTagValueMappingModel) {
    return super.getFilterValues(model).concat(model.name);
  }

  /**
   * Returns a unique list of all asset types represented in the mappings
   */
  @computed
  get assetTypes(): AssetType[] {
    return Array.from(this.get().reduce((acc, model) => acc.add(model.get('asset_type')), new Set())) as AssetType[];
  }

  /**
   * Checks all the asset types in the mappings for their fetched flag
   */
  @computed
  get assetTypesHaveFetched(): boolean {
    return this.assetTypes.every((assetType) => !!$assetTags.getAssetCollection(assetType)?.collection?.hasFetched);
  }

  /**
   * Breaks down usage by asset type
   */
  @computed
  get usage(): Record<AssetType, number> {
    return this.get().reduce(
      (acc, model) => {
        const { asset_type } = model.get() || {};

        if (!acc[asset_type]) {
          acc[asset_type] = 0;
        }

        acc[asset_type] += 1;

        return acc;
      },
      {} as Record<AssetType, number>
    );
  }

  /**
   * Returns the asset type with the highest usage count, falling back to device as the default
   */
  @computed
  get defaultAssetType(): AssetType {
    const usageItems = Object.entries(this.usage);

    if (usageItems.length === 0) {
      return AssetType.DEVICE;
    }

    return usageItems.reduce((max, current) => (current[1] > max[1] ? current : max))[0] as AssetType;
  }

  /**
   * Groups selects by asset type.
   * Used for loading selections into each asset type's own collection.
   */
  @computed
  get selectionsByAssetType() {
    return this.get().reduce(
      (acc, model) => {
        const assetType = model.get('asset_type');

        if (!acc[assetType]) {
          acc[assetType] = [];
        }

        acc[assetType].push(model.get('asset_id'));

        return acc;
      },
      {} as Record<AssetType, (string | number)[]>
    );
  }

  /**
   * Initializes selections into each asset type's own collection.
   * This basically sets up selections in the mapping dialog table.
   */
  @action
  initAssetSelections() {
    const { selectionsByAssetType } = this;

    // ensure we're working with a blank slate
    this.resetAssetCollections();

    Object.entries(selectionsByAssetType).forEach(([assetType, selections]) => {
      const { collection } = $assetTags.getAssetCollection(assetType as AssetType);

      if (collection && selections.length > 0) {
        const selectableModels = selections
          .map((id) => collection.get(id))
          .filter((model) => model && !!model.isSelectable) as AssetCollectionModel[];

        collection.selected = selectableModels;
      }
    });
  }

  /**
   * Replaces the mappings with the selections from each asset type's own collection.
   * Mappings that are already defined with a different value are filtered out.
   */
  @action
  setMappingsFromAssetSelections() {
    // assemble a complete list of selections across all asset types
    const nextSelections = Object.values(AssetType).flatMap((assetType) => {
      const { collection } = $assetTags.getAssetCollection(assetType);
      return (
        collection?.selectedArray
          .map((model) => ({ asset_type: assetType, asset_id: model.id }))
          .filter((item) => !this.valueModel.disabledMappings.has(`${assetType}:${item.asset_id}`)) || []
      );
    });

    // add items for removal via de-selection
    const mappingsToRemove = differenceWith(
      this.get(),
      nextSelections,
      (model, nextSelection) =>
        model.get('asset_type') === nextSelection.asset_type && model.get('asset_id') === nextSelection.asset_id
    );

    if (mappingsToRemove.length > 0) {
      this.valueModel.removeMapping(mappingsToRemove);
    }

    this.set(nextSelections);
  }

  /**
   * Puts all the state and selections for each asset type's own collection back at baseline.
   */
  @action
  resetAssetCollections() {
    Object.values(AssetType).forEach((assetType) => {
      const { collection } = $assetTags.getAssetCollection(assetType);

      if (collection) {
        collection.resetState();
        collection.clearSelection();
      }
    });
  }
}

export default AssetTagValueMappingCollection;
